import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Award, Clock, Target, TrendingUp, AlertTriangle,
  Download, Filter, Search, ChevronDown, BarChart3, PieChart, Activity,
  Eye, CheckCircle, XCircle, Loader2, RefreshCw, Globe, Lock, UserCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';
import PDFViewer from '../components/PDFViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const EnterpriseAnalyticsPage = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('company_token'));
  
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [applyingFallback, setApplyingFallback] = useState(false);
  const [legacySummary, setLegacySummary] = useState(null);
  
  // Bulk selection for status changes
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Fix Round 3 & 4 specific state
  const [fixingRounds, setFixingRounds] = useState(false);
  
  // Export with answers state
  const [exportingWithAnswers, setExportingWithAnswers] = useState(false);
  
  // Suspicious activity report state
  const [downloadingSuspiciousReport, setDownloadingSuspiciousReport] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score_desc');
  const [selectedRound, setSelectedRound] = useState('all');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchData();
  }, [token, challengeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('[Analytics] Fetching data for challenge:', challengeId);
      
      const [challengeRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/company/enterprise/challenges/${challengeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/company/enterprise/challenges/${challengeId}/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const challengeData = challengeRes.data.challenge || challengeRes.data;
      const candidatesData = analyticsRes.data.candidates || [];
      
      console.log('[Analytics] Challenge loaded:', challengeData?.title);
      console.log('[Analytics] Total candidates received:', candidatesData.length);
      console.log('[Analytics] Sample candidate:', candidatesData[0]);
      
      setChallenge(challengeData);
      setAnalytics(analyticsRes.data);
      setCandidates(candidatesData);
      setCurrentPage(1); // Reset to first page on new data
    } catch (err) {
      toast.error('Failed to load analytics');
      console.error('[Analytics] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    // Use filtered candidates if a specific round is selected, otherwise all candidates
    const dataToExport = selectedRound !== 'all' ? filteredCandidates : candidates;
    
    console.log('[CSV Export] selectedRound:', selectedRound);
    console.log('[CSV Export] Total candidates:', candidates.length);
    console.log('[CSV Export] Filtered candidates:', filteredCandidates.length);
    console.log('[CSV Export] Data to export:', dataToExport.length);
    
    if (!dataToExport.length) {
      toast.error('No candidates to export for the selected round');
      return;
    }
    
    // Include round name in export
    const getRoundNameForExport = (roundId) => {
      const round = challenge?.rounds?.find(r => r.id === roundId);
      return round?.name || roundId || 'N/A';
    };
    
    // Helper to extract PDF answer URLs from candidate data
    const getPdfAnswerUrls = (candidate) => {
      const urls = [];
      
      // Check submission_files array (primary source for advanced mode)
      if (candidate.submission_files && Array.isArray(candidate.submission_files)) {
        candidate.submission_files.forEach(sf => {
          if (sf.file_url) {
            let url = sf.file_url;
            if (url.startsWith('/api/')) {
              url = `https://evalentx.com${url}`;
            }
            urls.push(url);
          }
        });
      }
      
      // Check pdf_answers object (per-question uploads)
      if (candidate.pdf_answers && typeof candidate.pdf_answers === 'object') {
        Object.values(candidate.pdf_answers).forEach(pdf => {
          if (pdf && pdf.file_url) {
            let url = pdf.file_url;
            if (url.startsWith('/api/')) {
              url = `https://evalentx.com${url}`;
            }
            urls.push(url);
          }
        });
      }
      
      // Check answers for file URLs
      if (candidate.answers && typeof candidate.answers === 'object') {
        Object.values(candidate.answers).forEach(ans => {
          if (ans && typeof ans === 'object' && ans.file_url) {
            let url = ans.file_url;
            if (url.startsWith('/api/')) {
              url = `https://evalentx.com${url}`;
            }
            urls.push(url);
          }
        });
      }
      
      return urls.length > 0 ? urls.join(' | ') : '';
    };
    
    const headers = ['Name', 'Email', 'Round', 'Score', 'Total Points', 'Percentage', 'Time Taken (min)', 'Status', 'Result', 'Score Type', 'Rank (Fallback)', 'Violations', 'Submitted At', 'Status Changed By', 'Status Changed At', 'PDF Answer URLs'];
    const rows = dataToExport.map(c => [
      `"${(c.name || '').replace(/"/g, '""')}"`,  // Escape quotes in names
      c.email,
      getRoundNameForExport(c.round_id),
      c.score || 0,
      c.total_points || 0,
      c.percentage || 0,
      Math.round((c.time_taken || 0) / 60),
      c.status,
      c.qualification_status || (c.passed ? 'qualified' : 'rejected'),  // Qualification status
      c.score_type || 'actual',  // Score type: "actual" or "fallback"
      c.fallback_rank || '',      // Rank for fallback scores
      c.violations_count || 0,
      c.submitted_at || '',
      c.status_changed_by || '',  // Who changed the status
      c.status_changed_at || '',   // When status was changed
      `"${getPdfAnswerUrls(c)}"`  // PDF Answer URLs
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const roundSuffix = selectedRound !== 'all' ? `_${getRoundNameForExport(selectedRound)}` : '_all_rounds';
    a.download = `${challenge?.title || 'analytics'}${roundSuffix}_results.csv`;
    a.click();
    toast.success(`Exported ${dataToExport.length} candidates`);
  };

  // Export CSV with detailed answers (MCQ options, subjective answers, etc.)
  const exportWithAnswers = async () => {
    if (selectedRound === 'all') {
      toast.error('Please select a specific round to export with answers');
      return;
    }
    
    try {
      setExportingWithAnswers(true);
      toast.info('Fetching detailed answers for all candidates...');
      
      const response = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${selectedRound}/export-with-answers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      console.log('[Export With Answers] Data:', data);
      
      if (!data.candidates || data.candidates.length === 0) {
        toast.error('No candidates found for this round');
        return;
      }
      
      // Build CSV with questions as columns
      const questions = data.questions_meta || [];
      
      // Build headers
      const baseHeaders = ['Rank', 'Name', 'Email', 'College', 'Phone', 'Score', 'Total Points', 'Percentage', 'Status', 'Time (min)', 'Violations', 'Submitted At'];
      
      // Add question headers with dedicated PDF Link columns
      const questionHeaders = [];
      questions.forEach((q, idx) => {
        const qNum = `Q${idx + 1}`;
        const qTitle = (q.title || '').substring(0, 30);
        questionHeaders.push(`${qNum}: ${qTitle} (${q.type})`);
        questionHeaders.push(`${qNum} Answer`);
        questionHeaders.push(`${qNum} PDF Link`);  // Dedicated PDF link column per question
        if (q.type === 'mcq') {
          questionHeaders.push(`${qNum} Correct Answer`);
          questionHeaders.push(`${qNum} Options`);
        } else if (q.type === 'subjective') {
          questionHeaders.push(`${qNum} Expected Answer`);
        }
        questionHeaders.push(`${qNum} Points Earned`);
        questionHeaders.push(`${qNum} Is Correct`);
      });
      
      const allHeaders = [...baseHeaders, ...questionHeaders];
      
      // Build rows
      const rows = data.candidates.map((c, idx) => {
        const baseRow = [
          idx + 1,
          `"${(c.name || '').replace(/"/g, '""')}"`,
          c.email || '',
          `"${(c.college || '').replace(/"/g, '""')}"`,
          c.phone || '',
          c.score || 0,
          c.total_points || 0,
          c.percentage || 0,
          c.status || '',
          c.time_taken_minutes || 0,
          c.violations || 0,
          c.submitted_at || ''
        ];
        
        // Add question answers
        const questionAnswers = [];
        questions.forEach((qMeta, qIdx) => {
          const qAnswer = c.questions?.find(qa => qa.question_id === qMeta.id) || {};
          
          // Question title (already in header, but add type for clarity)
          questionAnswers.push(qMeta.type);
          
          // Submitted answer - escape quotes and newlines
          const submittedAns = (qAnswer.submitted_answer || '(No answer)').toString().replace(/"/g, '""').replace(/\n/g, ' ');
          questionAnswers.push(`"${submittedAns}"`);
          
          // PDF Link - Format: https://evalentx.com/api/enterprise/files/enterprise/{challengeId}/submissions/{submissionId}/answer_{fileId}.pdf
          // For MCQs this will be blank, for subjective/PDF questions show the link if available
          let pdfLink = '';
          if (qAnswer.pdf_answer_url) {
            // Use the URL directly if already in correct format
            let url = qAnswer.pdf_answer_url;
            // Ensure it uses the production domain
            if (url.startsWith('/api/')) {
              url = `https://evalentx.com${url}`;
            } else if (!url.startsWith('http')) {
              url = `https://evalentx.com/${url}`;
            }
            pdfLink = url;
          }
          // MCQs don't have PDF answers, leave blank
          if (qMeta.type === 'mcq') {
            pdfLink = '';
          }
          questionAnswers.push(pdfLink ? `"${pdfLink}"` : '');
          
          if (qMeta.type === 'mcq') {
            // Correct answer
            questionAnswers.push(qMeta.correct_answer || '');
            // Options
            const options = (qMeta.options || []).join(' | ');
            questionAnswers.push(`"${options}"`);
          } else if (qMeta.type === 'subjective') {
            // Expected answer
            const expected = (qMeta.expected_answer || '').toString().replace(/"/g, '""').replace(/\n/g, ' ');
            questionAnswers.push(`"${expected}"`);
          }
          
          // Points earned
          questionAnswers.push(qAnswer.points_earned || 0);
          // Is correct
          questionAnswers.push(qAnswer.is_correct ? 'Yes' : 'No');
        });
        
        return [...baseRow, ...questionAnswers];
      });
      
      // Generate CSV
      const csv = [allHeaders, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${challenge?.title || 'analytics'}_${data.round_name || 'round'}_detailed_answers.csv`;
      a.click();
      
      toast.success(`Exported ${data.candidates.length} candidates with ${questions.length} questions`);
      
    } catch (err) {
      console.error('Export with answers failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to export with answers');
    } finally {
      setExportingWithAnswers(false);
    }
  };

  // Download Suspicious Activity Report
  const downloadSuspiciousActivityReport = async () => {
    try {
      setDownloadingSuspiciousReport(true);
      toast.info('Generating suspicious activity report...');
      
      const response = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/suspicious-activity-report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const report = response.data;
      
      // Generate a readable text report
      let textReport = `SUSPICIOUS ACTIVITY REPORT\n`;
      textReport += `${'='.repeat(60)}\n`;
      textReport += `Challenge: ${report.challenge_title}\n`;
      textReport += `Generated: ${new Date(report.generated_at).toLocaleString()}\n`;
      textReport += `Total Rounds: ${report.summary?.total_rounds || 0}\n`;
      textReport += `Total Findings: ${report.summary?.total_findings || 0}\n`;
      textReport += `${'='.repeat(60)}\n\n`;
      
      for (const round of report.rounds || []) {
        textReport += `\n${'─'.repeat(60)}\n`;
        textReport += `ROUND: ${round.round_name}\n`;
        textReport += `Total Attempts: ${round.total_attempts}\n`;
        textReport += `Duration: ${round.duration_minutes} minutes\n`;
        textReport += `${'─'.repeat(60)}\n`;
        
        if (round.suspicious_findings?.length > 0) {
          textReport += `\n⚠️ FINDINGS:\n`;
          round.suspicious_findings.forEach(f => {
            textReport += `  • ${f}\n`;
          });
        }
        
        // Proctoring Violations
        const violations = round.proctoring_violations;
        if (violations?.total_with_violations > 0) {
          textReport += `\n🚨 PROCTORING VIOLATIONS:\n`;
          textReport += `   Total candidates with violations: ${violations.total_with_violations}\n`;
          textReport += `   Violation Types:\n`;
          for (const [type, count] of Object.entries(violations.violation_types || {})) {
            textReport += `     - ${type}: ${count}\n`;
          }
          
          if (violations.high_violation_candidates?.length > 0) {
            textReport += `\n   HIGH VIOLATION CANDIDATES:\n`;
            violations.high_violation_candidates.forEach(c => {
              textReport += `     • ${c.email} (${c.name})\n`;
              textReport += `       Violations: ${c.total_violations} (Critical: ${c.critical}, High: ${c.high})\n`;
              textReport += `       Types: ${c.types?.join(', ')}\n`;
            });
          }
        }
        
        // Suspiciously Fast
        if (round.timing_analysis?.suspiciously_fast?.length > 0) {
          textReport += `\n⏱️ SUSPICIOUSLY FAST COMPLETIONS:\n`;
          round.timing_analysis.suspiciously_fast.forEach(c => {
            textReport += `   • ${c.email} (${c.name})\n`;
            textReport += `     Time: ${c.time_minutes} min (expected: ${c.expected_duration} min)\n`;
            textReport += `     Score: ${c.score}/${c.total_points} (${c.percentage}%)\n`;
          });
        }
        
        // Shared IPs
        if (round.ip_analysis?.shared_ips?.length > 0) {
          textReport += `\n🌐 SHARED IP ADDRESSES:\n`;
          round.ip_analysis.shared_ips.forEach(ip => {
            textReport += `   • ${ip.ip_address} (${ip.candidate_count} candidates)\n`;
            ip.candidates?.forEach(c => {
              textReport += `     - ${c.email}: Score ${c.score}\n`;
            });
          });
        }
        
        // Duplicate Patterns
        if (round.answer_patterns?.duplicate_patterns?.length > 0) {
          textReport += `\n📝 DUPLICATE ANSWER PATTERNS:\n`;
          round.answer_patterns.duplicate_patterns.forEach(p => {
            textReport += `   • Pattern ${p.pattern_hash} (${p.candidate_count} candidates)\n`;
            p.candidates?.forEach(c => {
              textReport += `     - ${c.email}: Score ${c.score}\n`;
            });
          });
        }
        
        // Terminated
        if (round.terminated_attempts?.length > 0) {
          textReport += `\n🛑 TERMINATED ATTEMPTS:\n`;
          round.terminated_attempts.forEach(t => {
            textReport += `   • ${t.email}: ${t.reason}\n`;
          });
        }
        
        // Score Distribution
        textReport += `\n📊 SCORE DISTRIBUTION:\n`;
        textReport += `   Average: ${round.score_distribution?.avg_score || 0}\n`;
        textReport += `   Perfect Scores (≥95%): ${round.score_distribution?.perfect_scores || 0}\n`;
        textReport += `   High Scores (≥80%): ${round.score_distribution?.high_scores || 0}\n`;
      }
      
      textReport += `\n${'='.repeat(60)}\n`;
      textReport += `END OF REPORT\n`;
      
      // Download as text file
      const blob = new Blob([textReport], { type: 'text/plain;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${challenge?.title || 'challenge'}_suspicious_activity_report.txt`;
      a.click();
      
      // Also download JSON for detailed analysis
      const jsonBlob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const jsonUrl = URL.createObjectURL(jsonBlob);
      const jsonA = document.createElement('a');
      jsonA.href = jsonUrl;
      jsonA.download = `${challenge?.title || 'challenge'}_suspicious_activity_report.json`;
      jsonA.click();
      
      const totalFindings = report.summary?.total_findings || 0;
      if (totalFindings > 0) {
        toast.warning(`Report downloaded. Found ${totalFindings} suspicious activities!`);
      } else {
        toast.success('Report downloaded. No suspicious activities detected.');
      }
      
    } catch (err) {
      console.error('Failed to download suspicious activity report:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setDownloadingSuspiciousReport(false);
    }
  };

  // Recalculate all scores (fixes P0 scoring bug)
  const recalculateScores = async () => {
    if (!window.confirm('This will recalculate scores for all completed attempts. This may take a few minutes for large datasets. Continue?')) {
      return;
    }
    
    try {
      setRecalculating(true);
      const roundParam = selectedRound !== 'all' ? `?round_id=${selectedRound}` : '';
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/recalculate-scores${roundParam}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show appropriate message based on results
      if (res.data.skipped_fallback > 0) {
        toast.success(`Recalculated ${res.data.recalculated} attempts (preserved ${res.data.skipped_fallback} fallback scores)`);
      } else {
        toast.success(`Recalculated ${res.data.recalculated} attempts`);
      }
      
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} attempts had errors during recalculation`);
      }
      
      // Refresh data to show updated scores
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recalculate scores');
    } finally {
      setRecalculating(false);
    }
  };

  // Apply fallback scores to legacy attempts
  const applyFallbackScores = async (dryRun = false) => {
    // No confirmation popup - just apply directly
    try {
      setApplyingFallback(true);
      const roundParam = selectedRound !== 'all' ? `round_id=${selectedRound}&` : '';
      const response = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/apply-fallback-scores?${roundParam}dry_run=${dryRun}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (dryRun) {
        // Show preview
        const result = response.data;
        const message = result.rounds_processed.map(r => 
          `${r.round_name}: ${r.legacy_attempts_found} legacy attempts found`
        ).join('\n');
        
        if (result.total_legacy_attempts === 0) {
          toast.info('No legacy attempts found that need fallback scores');
        } else {
          toast.success(
            `Preview: ${result.total_legacy_attempts} legacy attempts would receive fallback scores.\n${message}`,
            { duration: 5000 }
          );
        }
        setLegacySummary(result);
      } else {
        toast.success(`Scores applied to ${response.data.total_scores_applied} attempts. Score is based on number of correct answers given by candidates.`);
        await fetchData(); // Refresh data
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply fallback scores');
    } finally {
      setApplyingFallback(false);
    }
  };

  // Fetch legacy attempts summary
  const fetchLegacySummary = async () => {
    if (!challengeId || !token) return;
    try {
      const response = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/legacy-attempts-summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[Legacy Summary] Fetched:', response.data);
      setLegacySummary(response.data);
    } catch (err) {
      console.error('Failed to fetch legacy summary:', err);
    }
  };

  // Change status for a single candidate
  const changeStatus = async (attemptId, newStatus) => {
    try {
      setChangingStatus(true);
      await axios.put(
        `${API}/api/company/enterprise/attempts/${attemptId}/status`,
        { new_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status changed to ${newStatus}`);
      await fetchData(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  // Bulk change status for selected candidates
  const bulkChangeStatus = async (newStatus) => {
    if (selectedCandidates.length === 0) {
      toast.error('No candidates selected');
      return;
    }
    
    try {
      setChangingStatus(true);
      const response = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/bulk-status-update`,
        { 
          attempt_ids: selectedCandidates,
          new_status: newStatus 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Updated ${response.data.updated} candidates to ${newStatus}`);
      setSelectedCandidates([]); // Clear selection
      await fetchData(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk update status');
    } finally {
      setChangingStatus(false);
    }
  };

  // Toggle candidate selection
  const toggleCandidateSelection = (attemptId) => {
    setSelectedCandidates(prev => 
      prev.includes(attemptId) 
        ? prev.filter(id => id !== attemptId)
        : [...prev, attemptId]
    );
  };

  // Select/deselect all visible candidates
  const toggleSelectAll = () => {
    const visibleIds = paginatedCandidates.map(c => c.id);
    const allSelected = visibleIds.every(id => selectedCandidates.includes(id));
    
    if (allSelected) {
      setSelectedCandidates(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedCandidates(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  // Check for legacy attempts on load - run when challenge loads
  useEffect(() => {
    if (challengeId && token) {
      fetchLegacySummary();
    }
  }, [challengeId, token]);

  // Filter and sort candidates
  const filteredCandidates = candidates
    .filter(c => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!c.name?.toLowerCase().includes(q) && !c.email?.toLowerCase().includes(q)) {
          return false;
        }
      }
      if (statusFilter !== 'all') {
        if (statusFilter === 'passed' && !c.passed) return false;
        if (statusFilter === 'failed' && c.passed) return false;
        if (statusFilter === 'suspicious' && (c.violations_count || 0) < 3) return false;
      }
      if (selectedRound !== 'all' && c.round_id !== selectedRound) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'score_desc': return (b.score || 0) - (a.score || 0);
        case 'score_asc': return (a.score || 0) - (b.score || 0);
        case 'time_asc': return (a.time_taken || 0) - (b.time_taken || 0);
        case 'time_desc': return (b.time_taken || 0) - (a.time_taken || 0);
        case 'violations': return (b.violations_count || 0) - (a.violations_count || 0);
        default: return 0;
      }
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCandidates.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, selectedRound, sortBy]);

  // Get access type display
  const getAccessTypeInfo = (accessType) => {
    switch (accessType) {
      case 'private':
        return { icon: Lock, label: 'Private', color: 'text-red-600 bg-red-50' };
      case 'invite':
        return { icon: UserCheck, label: 'Invite Only', color: 'text-amber-600 bg-amber-50' };
      default:
        return { icon: Globe, label: 'Public', color: 'text-green-600 bg-green-50' };
    }
  };

  // Get round name by ID
  const getRoundName = (roundId) => {
    const round = challenge?.rounds?.find(r => r.id === roundId);
    return round?.name || `Round ${roundId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-600" size={48} />
      </div>
    );
  }

  // Calculate stats
  const totalCandidates = candidates.length;
  const completedCount = candidates.filter(c => c.status === 'completed').length;
  const passedCount = candidates.filter(c => c.passed).length;
  const avgScore = totalCandidates > 0 
    ? Math.round(candidates.reduce((sum, c) => sum + (c.percentage || 0), 0) / totalCandidates) 
    : 0;
  const avgTime = totalCandidates > 0
    ? Math.round(candidates.reduce((sum, c) => sum + (c.time_taken || 0), 0) / totalCandidates / 60)
    : 0;
  const suspiciousCount = candidates.filter(c => (c.violations_count || 0) >= 3).length;

  // Score distribution
  const scoreDistribution = [
    { range: '0-20%', count: candidates.filter(c => (c.percentage || 0) <= 20).length },
    { range: '21-40%', count: candidates.filter(c => (c.percentage || 0) > 20 && (c.percentage || 0) <= 40).length },
    { range: '41-60%', count: candidates.filter(c => (c.percentage || 0) > 40 && (c.percentage || 0) <= 60).length },
    { range: '61-80%', count: candidates.filter(c => (c.percentage || 0) > 60 && (c.percentage || 0) <= 80).length },
    { range: '81-100%', count: candidates.filter(c => (c.percentage || 0) > 80).length },
  ];
  const maxDistribution = Math.max(...scoreDistribution.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/company/enterprise/challenges/${challengeId}`)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{challenge?.title} - Analytics</h1>
                <p className="text-sm text-gray-500">{totalCandidates} candidates</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw size={18} />
              </button>
              <button
                onClick={recalculateScores}
                disabled={recalculating}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm disabled:opacity-50"
                title="Recalculate all scores"
              >
                {recalculating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                {recalculating ? 'Recalculating...' : 'Fix Scores'}
              </button>
              {/* Fallback Scores Button - always visible for previous assessments */}
              <button
                onClick={() => applyFallbackScores(false)}
                disabled={applyingFallback}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm disabled:opacity-50"
                title="Apply scores based on number of correct answers given by candidates"
              >
                {applyingFallback ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                {applyingFallback ? 'Applying...' : 'Apply Fallback Scores'}
              </button>
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
              >
                <Download size={16} />
                Export CSV
              </button>
              {/* Export with Detailed Answers - Only shows when a specific round is selected */}
              {selectedRound !== 'all' && (
                <button
                  onClick={exportWithAnswers}
                  disabled={exportingWithAnswers}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm disabled:opacity-50"
                  title="Export CSV with all submitted answers, MCQ options, and expected answers"
                >
                  {exportingWithAnswers ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {exportingWithAnswers ? 'Exporting...' : 'Export with Answers'}
                </button>
              )}
              {/* Suspicious Activity Report Button */}
              <button
                onClick={downloadSuspiciousActivityReport}
                disabled={downloadingSuspiciousReport}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm disabled:opacity-50"
                title="Download suspicious activity report (proctoring violations, shared IPs, duplicate answers, etc.)"
              >
                {downloadingSuspiciousReport ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                {downloadingSuspiciousReport ? 'Generating...' : 'Suspicious Activity'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Legacy Attempts Warning Banner */}
        {legacySummary?.rounds?.some(r => r.needs_fallback > 0) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800">Legacy Assessments Detected</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Some assessments have missing answer data due to an earlier system issue. 
                  You can apply scores based on number of correct answers given by candidates.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {legacySummary.rounds.filter(r => r.needs_fallback > 0).map(r => (
                    <span key={r.round_id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {r.round_name}: {r.needs_fallback} legacy attempts
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => applyFallbackScores(true)}
                    disabled={applyingFallback}
                    className="text-xs px-3 py-1.5 bg-white border border-amber-300 text-amber-700 rounded hover:bg-amber-50"
                  >
                    Preview Fallback Scores
                  </button>
                  <button
                    onClick={() => applyFallbackScores(false)}
                    disabled={applyingFallback}
                    className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700"
                  >
                    {applyingFallback ? 'Applying...' : 'Apply Fallback Scores'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalCandidates}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{passedCount}</p>
                <p className="text-xs text-gray-500">Passed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Target size={20} className="text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{avgScore}%</p>
                <p className="text-xs text-gray-500">Avg Score</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{avgTime}m</p>
                <p className="text-xs text-gray-500">Avg Time</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{suspiciousCount}</p>
                <p className="text-xs text-gray-500">Suspicious</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Score Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-purple-600" />
              Score Distribution
            </h3>
            <div className="space-y-3">
              {scoreDistribution.map((d, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-16">{d.range}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                      style={{ width: `${(d.count / maxDistribution) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-8">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Pass/Fail Ratio */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart size={18} className="text-purple-600" />
              Pass Rate
            </h3>
            <div className="flex items-center justify-center">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64" cy="64" r="56"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="16"
                  />
                  <circle
                    cx="64" cy="64" r="56"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="16"
                    strokeDasharray={`${(passedCount / Math.max(completedCount, 1)) * 352} 352`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900">
                    {completedCount > 0 ? Math.round((passedCount / completedCount) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-4 text-sm">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                Passed: {passedCount}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gray-300 rounded-full" />
                Failed: {completedCount - passedCount}
              </span>
            </div>
          </div>
          
          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-purple-600" />
              Top Performers
            </h3>
            <div className="space-y-3">
              {candidates
                .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
                .slice(0, 5)
                .map((c, idx) => (
                  <div key={c.id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-amber-400 text-amber-900' :
                      idx === 1 ? 'bg-gray-300 text-gray-700' :
                      idx === 2 ? 'bg-amber-600 text-amber-100' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-500 truncate">{c.email}</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600">{c.percentage || 0}%</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">All Candidates</h3>
              <p className="text-xs text-gray-500 mt-1">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredCandidates.length)} of {filteredCandidates.length} 
                {candidates.length !== filteredCandidates.length && ` (${candidates.length} total)`}
              </p>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 pr-4 py-2 border rounded-lg text-sm w-48"
                />
              </div>
              
              {/* Round Filter */}
              <select
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Rounds</option>
                {challenge?.rounds?.map((round, idx) => (
                  <option key={round.id} value={round.id}>
                    Round {idx + 1}: {round.name}
                  </option>
                ))}
              </select>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="passed">Qualified</option>
                <option value="failed">Rejected</option>
                <option value="suspicious">Suspicious</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="score_desc">Score (High to Low)</option>
                <option value="score_asc">Score (Low to High)</option>
                <option value="time_asc">Time (Fast to Slow)</option>
                <option value="time_desc">Time (Slow to Fast)</option>
                <option value="violations">Most Violations</option>
              </select>
            </div>
          </div>
          
          {/* Round Info with Access Type */}
          {challenge?.rounds?.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-b flex gap-4 overflow-x-auto">
              {challenge.rounds.map((round, idx) => {
                const accessInfo = getAccessTypeInfo(round.access_type);
                const AccessIcon = accessInfo.icon;
                return (
                  <div 
                    key={round.id}
                    onClick={() => setSelectedRound(selectedRound === round.id ? 'all' : round.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                      selectedRound === round.id 
                        ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                        : 'bg-white border hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium">R{idx + 1}: {round.name}</span>
                    <span className={`px-1.5 py-0.5 rounded flex items-center gap-1 ${accessInfo.color}`}>
                      <AccessIcon size={10} /> {accessInfo.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Bulk Actions Bar */}
          {selectedCandidates.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <span className="text-sm text-purple-700">
                <strong>{selectedCandidates.length}</strong> candidate(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => bulkChangeStatus('qualified')}
                  disabled={changingStatus}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <CheckCircle size={14} />
                  Mark Qualified
                </button>
                <button
                  onClick={() => bulkChangeStatus('rejected')}
                  disabled={changingStatus}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <XCircle size={14} />
                  Mark Rejected
                </button>
                <button
                  onClick={() => setSelectedCandidates([])}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={paginatedCandidates.length > 0 && paginatedCandidates.every(c => selectedCandidates.includes(c.id))}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Round</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Violations</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedCandidates.map((c, idx) => (
                  <tr key={c.id || idx} className={`hover:bg-gray-50 ${selectedCandidates.includes(c.id) ? 'bg-purple-50' : ''}`}>
                    <td className="px-2 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.includes(c.id)}
                        onChange={() => toggleCandidateSelection(c.id)}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">#{startIndex + idx + 1}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-500">{c.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                        {getRoundName(c.round_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {c.score || 0}/{c.total_points || 0}
                        </span>
                        <span className="text-xs text-gray-500">({c.percentage || 0}%)</span>
                        {/* Fallback score indicator */}
                        {c.score_type === 'fallback' && (
                          <span 
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                            title={`Score based on number of correct answers (#${c.fallback_rank})`}
                          >
                            #{c.fallback_rank}
                          </span>
                        )}
                        {/* Legacy indicator (no score yet) */}
                        {c.is_legacy && !c.score_type && (
                          <span 
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500"
                            title="Legacy attempt - no answer data available"
                          >
                            Legacy
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {Math.round((c.time_taken || 0) / 60)} min
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        c.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                        c.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {c.status === 'completed' ? 'Completed' :
                         c.status === 'in_progress' ? 'In Progress' : c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        c.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {c.passed ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {c.passed ? 'Qualified' : 'Rejected'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        (c.violations_count || 0) >= 5 ? 'bg-red-100 text-red-700' :
                        (c.violations_count || 0) >= 3 ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {(c.violations_count || 0) >= 3 && <AlertTriangle size={12} />}
                        {c.violations_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {c.score_type === 'fallback' ? (
                          <span 
                            className="text-gray-400 text-sm flex items-center gap-1 cursor-not-allowed opacity-50"
                            title="Score is based on number of correct answers given by candidates"
                          >
                            <Eye size={14} className="text-gray-400" /> 
                            <span className="line-through">View</span>
                          </span>
                        ) : (
                          <button
                            onClick={() => setSelectedCandidate(c)}
                            className="text-purple-600 hover:text-purple-700 text-sm flex items-center gap-1"
                          >
                            <Eye size={14} /> View
                          </button>
                        )}
                        {/* Status Change Button */}
                        <button
                          onClick={() => changeStatus(c.id, c.passed ? 'rejected' : 'qualified')}
                          disabled={changingStatus}
                          className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                            c.passed 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } disabled:opacity-50`}
                          title={c.passed ? 'Mark as Rejected' : 'Mark as Qualified'}
                        >
                          {c.passed ? <XCircle size={12} /> : <CheckCircle size={12} />}
                          {c.passed ? 'Reject' : 'Qualify'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {filteredCandidates.length > 0 && (
              <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {paginatedCandidates.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Users className="mx-auto mb-2 text-gray-300" size={32} />
                <p>No candidates found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Candidate Detail & Manual Scoring Modal */}
      {selectedCandidate && (
        <CandidateReviewModal
          candidate={selectedCandidate}
          challengeId={challengeId}
          token={token}
          onClose={() => setSelectedCandidate(null)}
          onScoreUpdate={fetchData}
        />
      )}
    </div>
  );
};

// Separate component for the review modal
const CandidateReviewModal = ({ candidate, challengeId, token, onClose, onScoreUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [scoreEdits, setScoreEdits] = useState({});
  const [activeTab, setActiveTab] = useState('answers');
  
  useEffect(() => {
    fetchAttemptDetail();
  }, [candidate.id]);
  
  const fetchAttemptDetail = async () => {
    try {
      setLoading(true);
      console.log('[Review] Fetching attempt detail for:', candidate.id);
      
      const res = await axios.get(
        `${API}/api/company/enterprise/attempts/${candidate.id}/detail`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );
      
      console.log('[Review] Attempt detail loaded:', res.data);
      setAttemptDetail(res.data);
      
      // Initialize score edits from current scores
      const edits = {};
      res.data.evaluated_answers?.forEach(ea => {
        edits[ea.question_id] = {
          points_earned: ea.points_earned || 0,
          is_correct: ea.is_correct || false,
          feedback: ea.feedback || '',
          remarks: ea.remarks || ''
        };
      });
      setScoreEdits(edits);
    } catch (err) {
      console.error('[Review] Error loading attempt detail:', err);
      console.error('[Review] Error response:', err.response?.data);
      
      let errorMessage = 'Failed to load candidate details';
      if (err.response?.status === 404) {
        errorMessage = err.response?.data?.detail || 'Attempt not found';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied - you may not have permission to view this attempt';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleScoreChange = (questionId, field, value) => {
    setScoreEdits(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };
  
  const saveScores = async () => {
    try {
      setSaving(true);
      
      // Send ALL scores from scoreEdits
      const updates = Object.entries(scoreEdits).map(([question_id, data]) => ({
        question_id,
        points_earned: parseFloat(data.points_earned) || 0,
        is_correct: data.is_correct === true || data.is_correct === 'true' || data.is_correct === 'yes',
        feedback: data.feedback || '',
        remarks: data.remarks || ''
      }));
      
      console.log('[SaveScores] Candidate ID:', candidate.id);
      console.log('[SaveScores] Updates count:', updates.length);
      
      if (updates.length === 0) {
        toast.error('No scores to update. Please modify at least one score.');
        return;
      }
      
      const requestConfig = { 
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      };
      
      let response;
      try {
        // Try PUT first
        response = await axios.put(
          `${API}/api/company/enterprise/attempts/${candidate.id}/manual-score`,
          updates,
          requestConfig
        );
      } catch (putErr) {
        console.log('[SaveScores] PUT failed, trying POST:', putErr.message);
        // Fallback to POST (some proxies block PUT)
        response = await axios.post(
          `${API}/api/company/enterprise/attempts/${candidate.id}/manual-score`,
          updates,
          requestConfig
        );
      }
      
      console.log('[SaveScores] Success response:', response.data);
      toast.success(`Scores updated: ${response.data.new_score}/${response.data.total_points} (${response.data.percentage}%)`);
      await fetchAttemptDetail();
      onScoreUpdate();
    } catch (err) {
      console.error('[SaveScores] Error:', err);
      
      // Try individual updates as last resort
      try {
        console.log('[SaveScores] Trying individual updates...');
        const updates = Object.entries(scoreEdits);
        let successCount = 0;
        let lastResponse = null;
        
        for (const [question_id, data] of updates) {
          try {
            const params = new URLSearchParams({
              question_id,
              points: (parseFloat(data.points_earned) || 0).toString(),
              is_correct: (data.is_correct === true || data.is_correct === 'true').toString(),
              feedback: data.feedback || ''
            });
            
            lastResponse = await axios.post(
              `${API}/api/company/enterprise/attempts/${candidate.id}/simple-score?${params}`,
              null,
              { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
            );
            successCount++;
          } catch (singleErr) {
            console.error(`[SaveScores] Failed Q ${question_id}:`, singleErr.message);
          }
        }
        
        if (successCount > 0) {
          toast.success(`Updated ${successCount} score(s). Total: ${lastResponse?.data?.new_score || 'N/A'}`);
          await fetchAttemptDetail();
          onScoreUpdate();
          return;
        }
      } catch (fallbackErr) {
        console.error('[SaveScores] Fallback also failed:', fallbackErr);
      }
      
      let errorMsg = 'Failed to save scores. ';
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMsg += 'Network error - please check your connection.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'mcq': return '○';
      case 'coding': return '</>';
      case 'subjective': return '✎';
      case 'numeric': return '#';
      case 'mathematical': return '∑';
      default: return '?';
    }
  };
  
  const getQuestionTypeBadge = (type) => {
    const colors = {
      mcq: 'bg-blue-100 text-blue-700',
      coding: 'bg-purple-100 text-purple-700',
      subjective: 'bg-amber-100 text-amber-700',
      numeric: 'bg-green-100 text-green-700',
      mathematical: 'bg-indigo-100 text-indigo-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
          <p className="text-gray-600 mt-4">Loading candidate details...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold text-purple-600">
                {attemptDetail?.participant_name?.charAt(0) || '?'}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{attemptDetail?.participant_name}</h3>
              <p className="text-sm text-gray-500">{attemptDetail?.participant_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Only show Save Scores for non-fallback attempts */}
            {attemptDetail?.score_type !== 'fallback' && (
              <button
                onClick={saveScores}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Save Scores
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
              <XCircle size={20} />
            </button>
          </div>
        </div>
        
        {/* Summary Bar */}
        <div className="p-4 bg-gray-50 border-b flex items-center gap-6 flex-wrap shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Score:</span>
            <span className="font-bold text-lg text-purple-600">
              {attemptDetail?.score || 0}/{attemptDetail?.total_points || 0}
            </span>
            <span className="text-sm text-gray-500">({attemptDetail?.percentage || 0}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              attemptDetail?.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {attemptDetail?.qualification_status || 'pending'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Cutoff:</span>
            <span className="font-medium">{attemptDetail?.cutoff_marks || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Questions:</span>
            <span className="font-medium">
              {attemptDetail?.summary?.correct || 0} correct, {attemptDetail?.summary?.incorrect || 0} incorrect
              {attemptDetail?.summary?.needs_review > 0 && `, ${attemptDetail?.summary?.needs_review} needs review`}
            </span>
          </div>
        </div>
        
        {/* Tabs - simplified for fallback scores */}
        <div className="border-b px-4 shrink-0">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('answers')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'answers' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {attemptDetail?.score_type === 'fallback' ? 'Score Summary' : `Answers & Scoring (${attemptDetail?.evaluated_answers?.length || 0})`}
            </button>
            {/* Advanced Submissions Tab - Show only for advanced mode submissions */}
            {attemptDetail?.advanced_submission && attemptDetail?.submission_files?.length > 0 && (
              <button
                onClick={() => setActiveTab('submissions')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'submissions' 
                    ? 'border-amber-600 text-amber-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📎 Submitted Files ({attemptDetail?.submission_files?.length || 0})
              </button>
            )}
            {attemptDetail?.score_type !== 'fallback' && (
              <button
                onClick={() => setActiveTab('audit')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'audit' 
                    ? 'border-purple-600 text-purple-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Audit Trail
              </button>
            )}
            <button
              onClick={() => setActiveTab('info')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'info' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Candidate Info
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'answers' && (
            <div className="space-y-4">
              {/* Fallback score indicator - replaces answers view for fallback-scored attempts */}
              {attemptDetail?.score_type === 'fallback' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <Award className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-gray-800 text-lg mb-2">Score is based on number of correct answers given by candidates</h4>
                  <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                    This is a previous assessment where detailed answer data is not available. 
                    The score has been calculated based on the number of correct answers.
                  </p>
                  <div className="bg-white rounded-lg p-4 inline-block shadow-sm">
                    <div className="text-3xl font-bold text-purple-600">{attemptDetail.score}/{attemptDetail.total_points}</div>
                    <div className="text-sm text-gray-500">Score • Rank #{attemptDetail.fallback_rank}</div>
                  </div>
                  {attemptDetail.fallback_applied_at && (
                    <p className="text-xs text-gray-400 mt-4">
                      Applied on {new Date(attemptDetail.fallback_applied_at).toLocaleString()} 
                      {attemptDetail.fallback_applied_by && ` by ${attemptDetail.fallback_applied_by}`}
                    </p>
                  )}
                </div>
              )}
              
              {/* Legacy attempt warning banner - only show for non-fallback legacy attempts */}
              {attemptDetail?.data_status?.is_legacy_attempt && attemptDetail?.score_type !== 'fallback' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Legacy Assessment Data</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        This is an older assessment attempt where answer data was not recorded properly. 
                        This may have occurred due to a submission error or an earlier system issue.
                      </p>
                      <p className="text-sm text-amber-600 mt-2 font-medium">
                        Options: You can manually assign scores below, or ask the candidate to retake the assessment.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Warning for attempts with questions but no submitted answers */}
              {!attemptDetail?.data_status?.is_legacy_attempt && attemptDetail?.score_type !== 'fallback' && attemptDetail?.summary?.answered === 0 && attemptDetail?.evaluated_answers?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-800">No Answers Submitted</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        The candidate did not submit answers for any questions. This could indicate they abandoned the assessment or encountered technical issues.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Only show answers and manual scoring for non-fallback attempts */}
              {attemptDetail?.score_type !== 'fallback' && attemptDetail?.evaluated_answers?.map((answer, idx) => (
                <div key={answer.question_id} className="border rounded-lg overflow-hidden">
                  {/* Question Header */}
                  <div className="bg-gray-50 p-4 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 bg-white border rounded-lg flex items-center justify-center text-sm font-bold text-gray-600">
                        {idx + 1}
                      </span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getQuestionTypeBadge(answer.question_type)}`}>
                            {getQuestionTypeIcon(answer.question_type)} {answer.question_type?.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">
                            Max: {answer.max_points || answer.points || 10} pts
                          </span>
                          {answer.difficulty && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              answer.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                              answer.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {answer.difficulty}
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-gray-900">
                          {answer.question_title || answer.question_content?.substring(0, 100)}
                        </h4>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      answer.is_correct ? 'bg-green-100 text-green-700' :
                      answer.needs_manual_review ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {answer.is_correct ? '✓ Correct' : answer.needs_manual_review ? '⏳ Review' : '✗ Incorrect'}
                    </div>
                  </div>
                  
                  {/* Question Content (for coding/subjective) */}
                  {answer.question_content && answer.question_type !== 'mcq' && (
                    <div className="px-4 py-3 bg-gray-50 border-t">
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{answer.question_content}</p>
                    </div>
                  )}
                  
                  {/* Answer Section */}
                  <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Submitted Answer */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Submitted Answer
                      </label>
                      {answer.question_type === 'coding' ? (
                        <div className="rounded-lg border overflow-hidden" style={{ height: '250px' }}>
                          <Editor
                            height="100%"
                            language={answer.language || 'python'}
                            theme="vs-dark"
                            value={answer.submitted_answer || answer.original_answer || '// No code submitted'}
                            options={{
                              readOnly: true,
                              fontSize: 13,
                              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                              lineNumbers: 'on',
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              automaticLayout: true,
                              wordWrap: 'on',
                              padding: { top: 8, bottom: 8 }
                            }}
                          />
                        </div>
                      ) : (
                        <div className={`p-3 rounded-lg border ${
                          answer.question_type === 'coding' ? 'bg-gray-900 text-green-400 font-mono text-sm' : 'bg-gray-50'
                        }`}>
                          {answer.question_type === 'mcq' && answer.options?.length > 0 ? (
                            <div className="space-y-2">
                              {answer.options.map((opt, optIdx) => (
                                <div key={optIdx} className={`p-2 rounded ${
                                  opt === answer.submitted_answer ? 'bg-blue-100 border-blue-300 border' :
                                  opt === answer.correct_answer ? 'bg-green-50 border-green-300 border' : ''
                                }`}>
                                  <span className="font-medium mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                                  {opt}
                                  {opt === answer.submitted_answer && <span className="ml-2 text-blue-600">(Selected)</span>}
                                  {opt === answer.correct_answer && <span className="ml-2 text-green-600">(Correct)</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <pre className="whitespace-pre-wrap text-sm">
                              {answer.submitted_answer || answer.text_answer || answer.original_answer || '(No answer submitted)'}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Expected/Correct Answer */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expected/Correct Answer
                      </label>
                      <div className={`p-3 rounded-lg border ${
                        answer.question_type === 'coding' ? 'bg-gray-900 text-green-400 font-mono text-sm' : 'bg-green-50'
                      }`}>
                        {answer.question_type === 'subjective' ? (
                          <div className="space-y-3">
                            {/* Expected/Model Answer */}
                            {answer.expected_answer && (
                              <div>
                                <p className="text-sm font-medium text-green-800 mb-1">Expected/Model Answer:</p>
                                <div className="bg-white p-3 rounded border border-green-200">
                                  <pre className="whitespace-pre-wrap text-sm text-gray-700">{answer.expected_answer}</pre>
                                </div>
                              </div>
                            )}
                            {/* Key Points/Hints */}
                            {answer.expected_answer_hints && (
                              <div>
                                <p className="text-sm font-medium text-green-800 mb-1">Key Points/Hints:</p>
                                <p className="text-sm text-gray-600 bg-white p-2 rounded border border-green-200">{answer.expected_answer_hints}</p>
                              </div>
                            )}
                            {/* Rubric/Guidelines */}
                            {answer.rubric && (
                              <div>
                                <p className="text-sm font-medium text-green-800 mb-1">Rubric/Guidelines:</p>
                                <p className="text-sm text-gray-600 bg-white p-2 rounded border border-green-200">{answer.rubric}</p>
                              </div>
                            )}
                            {/* Show message if no guidance provided */}
                            {!answer.expected_answer && !answer.expected_answer_hints && !answer.rubric && (
                              <p className="text-gray-500 italic">No model answer or guidelines provided</p>
                            )}
                          </div>
                        ) : answer.question_type === 'coding' ? (
                          <div>
                            {answer.test_cases?.length > 0 ? (
                              <div className="space-y-2">
                                {answer.test_cases.slice(0, 3).map((tc, tcIdx) => (
                                  <div key={tcIdx} className="text-xs">
                                    <p>Input: {tc.input}</p>
                                    <p>Expected: {tc.expected_output}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">No test cases</p>
                            )}
                          </div>
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm">
                            {answer.correct_answer || answer.expected_answer || '(Not specified)'}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Manual Scoring Section */}
                  <div className="p-4 bg-purple-50 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Score (max: {answer.max_points || answer.points || 10})
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={answer.max_points || answer.points || 10}
                          step="0.5"
                          value={scoreEdits[answer.question_id]?.points_earned ?? answer.points_earned ?? 0}
                          onChange={(e) => handleScoreChange(answer.question_id, 'points_earned', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mark as Correct?
                        </label>
                        <select
                          value={scoreEdits[answer.question_id]?.is_correct ? 'yes' : 'no'}
                          onChange={(e) => handleScoreChange(answer.question_id, 'is_correct', e.target.value === 'yes')}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="yes">Yes - Correct</option>
                          <option value="no">No - Incorrect</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Feedback / Remarks
                        </label>
                        <input
                          type="text"
                          placeholder="Add feedback for the candidate..."
                          value={scoreEdits[answer.question_id]?.feedback ?? answer.feedback ?? ''}
                          onChange={(e) => handleScoreChange(answer.question_id, 'feedback', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                    
                    {/* Previous review info */}
                    {answer.manual_review_by && (
                      <div className="mt-3 text-xs text-gray-500">
                        Last reviewed by {answer.manual_review_by} on {new Date(answer.manual_review_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Warning banner for attempts with no submitted answers - hide for fallback scores */}
              {attemptDetail?.score_type !== 'fallback' && attemptDetail?.summary?.answered === 0 && attemptDetail?.evaluated_answers?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">No Submitted Answers Found</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        This attempt was completed but no answer data was recorded. This may happen with older assessments 
                        where the submission didn't save properly.
                      </p>
                      <p className="text-sm text-amber-600 mt-2">
                        You can still manually assign scores below. The candidate may need to retake the assessment for accurate evaluation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Empty state - hide for fallback scores since they show their own message */}
              {attemptDetail?.score_type !== 'fallback' && (!attemptDetail?.evaluated_answers || attemptDetail.evaluated_answers.length === 0) && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <XCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-700 mb-2">No Answers Found</h4>
                  <p className="text-gray-500 max-w-md mx-auto">
                    This attempt has no answer data available. This could be because:
                  </p>
                  <ul className="text-sm text-gray-500 mt-2 space-y-1">
                    <li>• The assessment is still in progress</li>
                    <li>• The submission encountered an error</li>
                    <li>• This is an older attempt with incomplete data</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Score Change History</h4>
              {attemptDetail?.evaluated_answers?.some(ea => ea.score_history?.length > 0) ? (
                attemptDetail.evaluated_answers.map((ea, idx) => (
                  ea.score_history?.length > 0 && (
                    <div key={ea.question_id} className="border rounded-lg p-4">
                      <h5 className="font-medium text-gray-700 mb-3">Question {idx + 1}</h5>
                      <div className="space-y-2">
                        {ea.score_history.map((history, hIdx) => (
                          <div key={hIdx} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                            <div>
                              <span className="font-medium">{history.reviewer_email}</span>
                              <span className="text-gray-500 mx-2">changed score from</span>
                              <span className="text-red-600">{history.previous_score}</span>
                              <span className="text-gray-500 mx-1">to</span>
                              <span className="text-green-600">{history.new_score}</span>
                              {history.remarks && (
                                <span className="text-gray-500 ml-2">- "{history.remarks}"</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(history.timestamp).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No score changes recorded yet
                </div>
              )}
            </div>
          )}
          
          {/* Submissions Tab - For Advanced Mode PDF/Image uploads */}
          {activeTab === 'submissions' && (
            <div className="space-y-6">
              {/* Needs Manual Evaluation Banner */}
              {attemptDetail?.needs_manual_evaluation && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Manual Evaluation Required</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        This is an advanced assessment with uploaded answer sheets. Please review the submitted files below and assign a score manually.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual Score Input for Advanced Submissions */}
              {attemptDetail?.advanced_submission && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-100">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Assign Manual Score
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Score (out of {attemptDetail.total_points})</label>
                      <input
                        type="number"
                        min="0"
                        max={attemptDetail.total_points || 100}
                        value={scoreEdits.manual_score || attemptDetail.score || ''}
                        onChange={(e) => setScoreEdits(prev => ({ ...prev, manual_score: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-400 outline-none"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Feedback / Remarks</label>
                      <input
                        type="text"
                        value={scoreEdits.manual_feedback || ''}
                        onChange={(e) => setScoreEdits(prev => ({ ...prev, manual_feedback: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-400 outline-none"
                        placeholder="Add evaluation notes..."
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await axios.put(
                          `${API}/api/company/enterprise/attempts/${candidate.id}/manual-score`,
                          { 
                            score: scoreEdits.manual_score || 0,
                            feedback: scoreEdits.manual_feedback || '',
                            evaluated_by: 'Company Admin'
                          },
                          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                        );
                        toast.success('Score saved successfully');
                        await fetchAttemptDetail();
                        onScoreUpdate();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to save score');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Save Score
                  </button>
                </div>
              )}
              
              {/* Submitted Files Grid */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4">Submitted Files</h4>
                <div className="space-y-4">
                  {attemptDetail?.submission_files?.map((file, idx) => (
                    <div key={file.file_id || idx} className="border rounded-xl overflow-hidden bg-white shadow-sm">
                      {/* File Preview */}
                      {file.type === 'image' ? (
                        <div className="bg-gray-100">
                          <img 
                            src={file.file_url} 
                            alt={file.file_name || `Image ${idx + 1}`}
                            className="w-full max-h-[500px] object-contain cursor-pointer hover:opacity-90"
                            onClick={() => window.open(file.file_url, '_blank')}
                          />
                          {/* Page Number Badge */}
                          {file.page_number && (
                            <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                              Page {file.page_number}
                            </div>
                          )}
                        </div>
                      ) : file.type === 'pdf' ? (
                        <PDFViewer 
                          url={file.file_url} 
                          fileName={file.file_name || 'answer.pdf'}
                          embedded={true}
                        />
                      ) : (
                        <div className="h-48 flex items-center justify-center bg-gray-50">
                          <span className="text-gray-400">Unknown file type</span>
                        </div>
                      )}
                      
                      {/* File Info & Actions */}
                      <div className="p-3 border-t bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-700 truncate" title={file.file_name}>
                              {file.file_name || `${file.type === 'pdf' ? 'Answer Sheet' : 'Image'} ${idx + 1}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString() : 'Uploaded'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => window.open(file.file_url, '_blank')}
                              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                              title="View Full Size"
                            >
                              <Eye size={16} className="text-gray-600" />
                            </button>
                            <a
                              href={file.file_url}
                              download={file.file_name}
                              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                              title="Download"
                            >
                              <Download size={16} className="text-gray-600" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Empty State */}
                {(!attemptDetail?.submission_files || attemptDetail.submission_files.length === 0) && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <XCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p>No files submitted for this assessment</p>
                  </div>
                )}
              </div>
              
              {/* Submission Metadata */}
              {attemptDetail?.advanced_submission && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Submission Details</h5>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 font-medium capitalize">{attemptDetail.submission_type || 'Mixed'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Files:</span>
                      <span className="ml-2 font-medium">{attemptDetail.submission_files?.length || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Evaluation:</span>
                      <span className={`ml-2 font-medium ${attemptDetail.evaluation_status === 'evaluated' ? 'text-green-600' : 'text-amber-600'}`}>
                        {attemptDetail.evaluation_status === 'evaluated' ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'info' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Name</p>
                <p className="font-medium">{attemptDetail?.participant_name}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Email</p>
                <p className="font-medium">{attemptDetail?.participant_email}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Phone</p>
                <p className="font-medium">{attemptDetail?.participant_phone || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">College</p>
                <p className="font-medium">{attemptDetail?.participant_college || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Round</p>
                <p className="font-medium">{attemptDetail?.round_name}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <p className="font-medium">{attemptDetail?.status}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Started At</p>
                <p className="font-medium">
                  {attemptDetail?.started_at ? new Date(attemptDetail.started_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Completed At</p>
                <p className="font-medium">
                  {attemptDetail?.completed_at ? new Date(attemptDetail.completed_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              
              {/* Proctoring Violations */}
              {attemptDetail?.proctoring_violations?.length > 0 && (
                <div className="col-span-2 bg-amber-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    Proctoring Violations ({attemptDetail.proctoring_violations.length})
                  </p>
                  <div className="space-y-2">
                    {attemptDetail.proctoring_violations.map((v, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-amber-700">{v.type}</span>
                        <span className="text-amber-600">{v.timestamp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseAnalyticsPage;
